import { useCallback, useEffect } from 'react';
import { Alert } from 'react-native';
import { useAppDispatch, useAppSelector } from '../store';
import { logger } from '../utils/logger';
import {
  fetchPeerProgressComparisons,
  fetchSocialLearningEffectiveness,
  fetchCollaborativeAchievements,
  generateEngagementOptimization,
  fetchActiveSocialABTests,
  createSocialLearningABTest,
  updatePrivacySettings,
  selectPeerComparisons,
  selectLearningEffectiveness,
  selectCollaborativeAchievements,
  selectEngagementOptimization,
  selectActiveSocialABTests,
  selectSocialAnalyticsLoading,
  selectSocialAnalyticsError,
  selectAnalyticsPrivacySettings,
  selectTopPerformingPeers,
  selectActiveAchievements,
  selectCompletedAchievements,
  selectHighImpactOptimizations,
  selectUserPerformanceInsights,
  updateAchievementProgress,
  markMilestoneAchieved,
  addCollaborativeAchievement,
  updateABTestMetrics,
  toggleAnalytics,
  type AnalyticsPrivacySettings,
  type SocialLearningABTest,
  type CollaborativeAchievement,
  type AchievementProgress,
  type VariantMetrics,
} from '../store/slices/socialLearningAnalyticsSlice';

export interface UseSocialLearningAnalyticsReturn {
  // State
  peerComparisons: ReturnType<typeof selectPeerComparisons>;
  learningEffectiveness: ReturnType<typeof selectLearningEffectiveness>;
  collaborativeAchievements: ReturnType<typeof selectCollaborativeAchievements>;
  engagementOptimization: ReturnType<typeof selectEngagementOptimization>;
  activeABTests: ReturnType<typeof selectActiveSocialABTests>;
  loading: ReturnType<typeof selectSocialAnalyticsLoading>;
  error: ReturnType<typeof selectSocialAnalyticsError>;
  privacySettings: ReturnType<typeof selectAnalyticsPrivacySettings>;

  // Computed data
  topPerformingPeers: ReturnType<typeof selectTopPerformingPeers>;
  activeAchievements: ReturnType<typeof selectActiveAchievements>;
  completedAchievements: ReturnType<typeof selectCompletedAchievements>;
  highImpactOptimizations: ReturnType<typeof selectHighImpactOptimizations>;
  userPerformanceInsights: ReturnType<typeof selectUserPerformanceInsights>;

  // Actions
  loadPeerComparisons: (userId: string) => Promise<void>;
  loadLearningEffectiveness: (userId: string, period: string) => Promise<void>;
  loadCollaborativeAchievements: (userId: string) => Promise<void>;
  generateOptimization: (userId: string) => Promise<void>;
  loadActiveABTests: (userId: string) => Promise<void>;
  createABTest: (
    testConfig: Omit<
      SocialLearningABTest,
      'id' | 'status' | 'results' | 'currentParticipants'
    >
  ) => Promise<void>;
  updatePrivacy: (
    userId: string,
    settings: Partial<AnalyticsPrivacySettings>
  ) => Promise<void>;
  updateAchievement: (
    achievementId: string,
    progress: Partial<AchievementProgress>
  ) => void;
  markMilestone: (achievementId: string, milestoneId: string) => void;
  addAchievement: (achievement: CollaborativeAchievement) => void;
  updateTestMetrics: (
    testId: string,
    variantId: string,
    metrics: Partial<VariantMetrics>
  ) => void;
  toggleAnalyticsEnabled: (enabled: boolean) => void;
  refreshAllData: (userId: string) => Promise<void>;
}

export function useSocialLearningAnalytics(): UseSocialLearningAnalyticsReturn {
  const dispatch = useAppDispatch();

  // Selectors
  const peerComparisons = useAppSelector(selectPeerComparisons);
  const learningEffectiveness = useAppSelector(selectLearningEffectiveness);
  const collaborativeAchievements = useAppSelector(
    selectCollaborativeAchievements
  );
  const engagementOptimization = useAppSelector(selectEngagementOptimization);
  const activeABTests = useAppSelector(selectActiveSocialABTests);
  const loading = useAppSelector(selectSocialAnalyticsLoading);
  const error = useAppSelector(selectSocialAnalyticsError);
  const privacySettings = useAppSelector(selectAnalyticsPrivacySettings);

  // Computed selectors
  const topPerformingPeers = useAppSelector(selectTopPerformingPeers);
  const activeAchievements = useAppSelector(selectActiveAchievements);
  const completedAchievements = useAppSelector(selectCompletedAchievements);
  const highImpactOptimizations = useAppSelector(selectHighImpactOptimizations);
  const userPerformanceInsights = useAppSelector(selectUserPerformanceInsights);

  // Actions
  const loadPeerComparisons = useCallback(
    async (userId: string) => {
      try {
        await dispatch(fetchPeerProgressComparisons(userId)).unwrap();
      } catch (error) {
        logger.error('Failed to load peer comparisons:', error);
        throw error;
      }
    },
    [dispatch]
  );

  const loadLearningEffectiveness = useCallback(
    async (userId: string, period: string = '30d') => {
      try {
        await dispatch(
          fetchSocialLearningEffectiveness({ userId, period })
        ).unwrap();
      } catch (error) {
        logger.error('Failed to load learning effectiveness:', error);
        throw error;
      }
    },
    [dispatch]
  );

  const loadCollaborativeAchievements = useCallback(
    async (userId: string) => {
      try {
        await dispatch(fetchCollaborativeAchievements(userId)).unwrap();
      } catch (error) {
        logger.error('Failed to load collaborative achievements:', error);
        throw error;
      }
    },
    [dispatch]
  );

  const generateOptimization = useCallback(
    async (userId: string) => {
      try {
        await dispatch(generateEngagementOptimization(userId)).unwrap();
        Alert.alert(
          'Success',
          'Engagement optimization recommendations generated!'
        );
      } catch (error) {
        logger.error('Failed to generate optimization:', error);
        Alert.alert(
          'Error',
          'Failed to generate optimization recommendations. Please try again.'
        );
        throw error;
      }
    },
    [dispatch]
  );

  const loadActiveABTests = useCallback(
    async (userId: string) => {
      try {
        await dispatch(fetchActiveSocialABTests(userId)).unwrap();
      } catch (error) {
        logger.error('Failed to load AB tests:', error);
        throw error;
      }
    },
    [dispatch]
  );

  const createABTest = useCallback(
    async (
      testConfig: Omit<
        SocialLearningABTest,
        'id' | 'status' | 'results' | 'currentParticipants'
      >
    ) => {
      try {
        await dispatch(createSocialLearningABTest(testConfig)).unwrap();
        Alert.alert('Success', 'A/B test created successfully!');
      } catch (error) {
        logger.error('Failed to create AB test:', error);
        Alert.alert('Error', 'Failed to create A/B test. Please try again.');
        throw error;
      }
    },
    [dispatch]
  );

  const updatePrivacy = useCallback(
    async (userId: string, settings: Partial<AnalyticsPrivacySettings>) => {
      try {
        await dispatch(updatePrivacySettings({ userId, settings })).unwrap();
        Alert.alert('Success', 'Privacy settings updated successfully!');
      } catch (error) {
        logger.error('Failed to update privacy settings:', error);
        Alert.alert(
          'Error',
          'Failed to update privacy settings. Please try again.'
        );
        throw error;
      }
    },
    [dispatch]
  );

  const updateAchievement = useCallback(
    (achievementId: string, progress: Partial<AchievementProgress>) => {
      dispatch(updateAchievementProgress({ achievementId, progress }));
    },
    [dispatch]
  );

  const markMilestone = useCallback(
    (achievementId: string, milestoneId: string) => {
      dispatch(markMilestoneAchieved({ achievementId, milestoneId }));
    },
    [dispatch]
  );

  const addAchievement = useCallback(
    (achievement: CollaborativeAchievement) => {
      dispatch(addCollaborativeAchievement(achievement));
    },
    [dispatch]
  );

  const updateTestMetrics = useCallback(
    (testId: string, variantId: string, metrics: Partial<VariantMetrics>) => {
      dispatch(updateABTestMetrics({ testId, variantId, metrics }));
    },
    [dispatch]
  );

  const toggleAnalyticsEnabled = useCallback(
    (enabled: boolean) => {
      dispatch(toggleAnalytics(enabled));
    },
    [dispatch]
  );

  const refreshAllData = useCallback(
    async (userId: string) => {
      try {
        await Promise.all([
          loadPeerComparisons(userId),
          loadLearningEffectiveness(userId, '30d'),
          loadCollaborativeAchievements(userId),
          loadActiveABTests(userId),
        ]);
      } catch (error) {
        logger.error(
          'Failed to refresh social learning analytics data:',
          error
        );
        throw error;
      }
    },
    [
      loadPeerComparisons,
      loadLearningEffectiveness,
      loadCollaborativeAchievements,
      loadActiveABTests,
    ]
  );

  return {
    // State
    peerComparisons,
    learningEffectiveness,
    collaborativeAchievements,
    engagementOptimization,
    activeABTests,
    loading,
    error,
    privacySettings,

    // Computed data
    topPerformingPeers,
    activeAchievements,
    completedAchievements,
    highImpactOptimizations,
    userPerformanceInsights,

    // Actions
    loadPeerComparisons,
    loadLearningEffectiveness,
    loadCollaborativeAchievements,
    generateOptimization,
    loadActiveABTests,
    createABTest,
    updatePrivacy,
    updateAchievement,
    markMilestone,
    addAchievement,
    updateTestMetrics,
    toggleAnalyticsEnabled,
    refreshAllData,
  };
}

// Utility hooks for specific analytics features
export function usePeerBenchmarking() {
  const peerComparisons = useAppSelector(selectPeerComparisons);
  const topPerformingPeers = useAppSelector(selectTopPerformingPeers);
  const userInsights = useAppSelector(selectUserPerformanceInsights);
  const loading = useAppSelector(
    state => state.socialLearningAnalytics.loading.comparisons
  );

  return {
    peerComparisons,
    topPerformingPeers,
    userInsights,
    loading,
  };
}

export function useCollaborativeAchievements() {
  const activeAchievements = useAppSelector(selectActiveAchievements);
  const completedAchievements = useAppSelector(selectCompletedAchievements);
  const loading = useAppSelector(
    state => state.socialLearningAnalytics.loading.achievements
  );

  return {
    activeAchievements,
    completedAchievements,
    loading,
  };
}

export function useEngagementOptimization() {
  const optimization = useAppSelector(selectEngagementOptimization);
  const highImpactOptimizations = useAppSelector(selectHighImpactOptimizations);
  const loading = useAppSelector(
    state => state.socialLearningAnalytics.loading.optimization
  );

  return {
    optimization,
    highImpactOptimizations,
    loading,
  };
}

export function useSocialABTesting() {
  const activeTests = useAppSelector(selectActiveSocialABTests);
  const loading = useAppSelector(
    state => state.socialLearningAnalytics.loading.abTests
  );

  return {
    activeTests,
    loading,
  };
}
